import { ColDef, ICellRendererParams } from 'ag-grid-community';
import { Card, CardContent, Typography, IconButton, Button } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { FunctionComponent, useMemo, useState } from 'react';
import { UserLocationDTO } from '../../../api/users/users.contracts';
import { useTranslation } from '@/lib';
import { PoGrid } from '@/components/grid/PoGrid';
import AddLocationModal from './AddLocationModal';
import { LocationModel } from '@/modules/locations/types/LocationModel';

interface AssignedLocationsTableProps {
  userLocations: UserLocationDTO[];
  onRemoveLocation: (locationId: number) => void;
  onAddLocation: (location: LocationModel) => void;
}

interface LocationRowData {
  id: number;
  locationId: number;
  locationName: string;
  locationCode: string;
}

const AssignedLocationsTable: FunctionComponent<AssignedLocationsTableProps> = ({ userLocations, onRemoveLocation, onAddLocation }) => {
  const { t } = useTranslation();
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);

  // Get current selected location IDs for the modal
  const selectedLocationIds = useMemo(() => {
    return userLocations.map((ul) => ul.locationId);
  }, [userLocations]);

  // Transform userLocations to row data
  const rowData: LocationRowData[] = useMemo(() => {
    return userLocations.map((ul) => ({
      id: ul.id,
      locationId: ul.locationId,
      locationName: ul.locationName || `Location ${ul.locationId}`, // Fallback if name not provided
      locationCode: ul.locationCode || `LOC-${ul.locationId}`, // Fallback if code not provided
    }));
  }, [userLocations]);

  // Remove button cell renderer
  const RemoveButtonRenderer = (params: ICellRendererParams<LocationRowData>) => {
    if (!params.data) return null;

    return (
      <div className="flex items-center justify-center h-full">
        <IconButton size="small" color="error" onClick={() => onRemoveLocation(params.data!.locationId)} aria-label={t('remove_location')}>
          <DeleteIcon fontSize="small" />
        </IconButton>
      </div>
    );
  };

  const columnDefs: ColDef<LocationRowData>[] = useMemo(() => {
    return [
      {
        headerName: t('location_name'),
        field: 'locationName',
        flex: 1,
        sortable: true,
        filter: true,
      },
      {
        headerName: t('location_code'),
        field: 'locationCode',
        width: 120,
        sortable: true,
        filter: true,
      },
      {
        headerName: '',
        colId: 'actions',
        width: 80,
        cellRenderer: RemoveButtonRenderer,
        sortable: false,
        filter: false,
        resizable: false,
      },
    ];
  }, [t]);

  return (
    <>
      <Card variant="outlined">
        <CardContent>
          <div className="flex justify-between items-center mb-2">
            <Typography variant="h6" color="textSecondary">
              {t('assigned_locations')}
            </Typography>
            <Button variant="outlined" color="primary" onClick={() => setIsAddModalOpen(true)} size="small">
              {t('add_location')}
            </Button>
          </div>
          <div className="h-96">
            <PoGrid
              rowData={rowData}
              colDefs={columnDefs}
              gridOptions={{
                domLayout: 'normal',
                suppressHorizontalScroll: false,
                rowHeight: 40,
                headerHeight: 40,
                noRowsOverlayComponent: () => (
                  <div className="flex items-center justify-center h-full text-gray-500">{t('no_locations_assigned')}</div>
                ),
              }}
            />
          </div>
        </CardContent>
      </Card>

      <AddLocationModal
        open={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onAddLocation={(location) => {
          onAddLocation(location);
          // Don't close modal - let user add more locations
        }}
        onRemoveLocation={(locationId) => {
          onRemoveLocation(locationId);
        }}
        selectedLocationIds={selectedLocationIds}
      />
    </>
  );
};

export default AssignedLocationsTable;
